import AddIcon from '@mui/icons-material/Add';
import { Alert, Box } from '@mui/material';
import { Button, SearchBar } from '@peritasai/ui-components';
import type { AssetCatalogItemEntity } from '@peritasai/repository';
import { useAssetCatalog } from '@peritasai/repository';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GridPaginationModel } from '@mui/x-data-grid-premium';
import { AssetCatalogDrawer } from './AssetCatalogDrawer';
import { DrawerMode } from '../../utils/drawer';
import { AssetCatalogTable } from './AssetCatalogTable';

export const AssetCatalogPage = () => {
  const { t } = useTranslation(['periverse', 'common']);
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>(DrawerMode.Create);
  const [selectedItem, setSelectedItem] = useState<AssetCatalogItemEntity | null>(null);

  const { data, isLoading, isError } = useAssetCatalog({
    search: activeSearch || undefined,
    page: paginationModel.page,
    limit: paginationModel.pageSize,
  });

  const rows = data?.assetCatalogItems ?? [];
  const rowCount = data?.total ?? 0;

  const handleInputChange = (value: string) => {
    setSearchInput(value);
    if (!value) {
      setActiveSearch('');
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleSearch = (value: string) => {
    setActiveSearch(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClear = () => {
    setSearchInput('');
    setActiveSearch('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRowClick = (row: AssetCatalogItemEntity) => {
    setSelectedItem(row);
    setDrawerMode(DrawerMode.Edit);
    setDrawerOpen(true);
  };

  const handleAddClick = () => {
    setSelectedItem(null);
    setDrawerMode(DrawerMode.Create);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
        <SearchBar
          value={searchInput}
          onChange={handleInputChange}
          onSearch={handleSearch}
          onClear={handleClear}
          placeholder={`${t('common:search')} ${t('pages.entityManagement.items.trackedAssetCatalog')}...`}
          width={{ xs: '100%', sm: '45%' }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{ flexShrink: 0 }}
        >
          {t('common.add')}
        </Button>
      </Box>

      {isError && (
        <Alert severity="error">
          {t('pages.entityManagement.loadError')}
        </Alert>
      )}

      <AssetCatalogTable
        rows={rows}
        rowCount={rowCount}
        isLoading={isLoading}
        hasSearch={activeSearch.length > 0}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        onRowClick={handleRowClick}
      />

      <AssetCatalogDrawer
        open={drawerOpen}
        mode={drawerMode}
        selectedItem={selectedItem}
        onClose={handleDrawerClose}
      />
    </Box>
  );
};
